import { channelInfo } from '@op/components/ChannelLabel';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { chartAllColors } from '@op/constants';
import { getPercentageValue, sortMapAuxiliaryFunc } from '@op/utils';
import { formatTimeToShow } from '@op/utils/time';
import { percentageFormat, reviewsFormat, yAxleFormat } from '@op/utils/value';
import React from 'react';
import { useMemo } from 'react';
import { useIntl } from 'umi';
import { channelColorMap } from '../utils';
import { hexToRgba } from '@op/utils/systemUtil';

const followerOpacityMap: any = {
  common: 0.4,
  nano: 0.5,
  micro: 0.6,
  'mid-tier': 0.8,
  macro: 0.9,
  mega: 1,
};

export default function HBarChart({
  filter,
  data,
  height = 140,
  title,
  sortMap,
  translate = (value: string) => value,
}: any) {
  const { formatMessage } = useIntl();
  function translateChannel(value: any) {
    const channel = channelInfo(value);
    return channel.name;
  }
  const options = useMemo(() => {
    const info: { [key: string]: any } = {};
    const keys = new Set<string>();
    const totalMap: { [key: string]: number } = {};
    data.forEach((item: { channel: string; name: any; value: number }) => {
      const { channel, name, value } = item;
      if (!info[channel]) info[channel] = {};
      keys.add(name);
      info[channel][name] = value || 0;
      totalMap[channel] = (totalMap[channel] || 0) + (value || 0);
    });
    const yAxis = Object.keys(totalMap);
    yAxis.sort(sortMapAuxiliaryFunc(totalMap, (item) => item));
    const series = Array.from(keys).map((key: string) => {
      const listData = yAxis.map((channel, index) => {
        const color = channelColorMap[channel] || chartAllColors[index];
        const newColor = hexToRgba(color, followerOpacityMap[key]);
        const borderColor = hexToRgba(color, followerOpacityMap[key] + 0.2);
        const detail: any = info[channel] || {};
        return {
          value: detail[key] || 0,
          itemStyle: {
            color: newColor,
            borderColor: borderColor,
            borderWidth: 1,
            padding: 1,
          },
        };
      });

      return {
        name: key,
        type: 'bar',
        stack: 'total',
        data: listData,
        barWidth: 8.6,
      };
    });
    if (sortMap) {
      series.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
    }
    return {
      legend: {
        show: false,
      },
      grid: {
        top: 0,
        bottom: 0,
      },
      tooltip: {
        formatter: (params: any) => {
          const time = formatTimeToShow(filter);

          return formatChartParamsDate({
            title: translateChannel(params?.[0]?.axisValueLabel),
            params,
            dealwithRecords(records, total) {
              records.sort(
                (a, b) => (b.originValue || 0) - (a.originValue || 0),
              );

              records = records.map((item) => {
                return {
                  ...item,
                  value: `${item.value}(${percentageFormat(
                    getPercentageValue(item?.originValue || 0, total, 6) * 100,
                  )})`,
                };
              });

              records.unshift({
                text: formatMessage({ id: 'Total' }),
                value: reviewsFormat(total),
                originValue: total,
                originText: 'Total',
              });

              return records;
            },
            listTitleFunc: (item) => translate(item.seriesName),
            valueFormat: (value) => reviewsFormat(value),
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    weight: 'bold',
                    header: time,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: title,
                  },
                ],
              },
            ],
          });
        },
        confine: false,
        position: (pos, params, dom: HTMLDivElement, rect, size) => {
          return {
            bottom: 0,
            left: rect?.width || 260,
          };
        },
      },
      xAxis: {
        type: 'value',
        splitNumber: 5,
        axisLabel: {
          fontSize: 8,
          color: "rgba(38, 40, 50, 0.6)",
          align: 'center',
          formatter: (value: number) => yAxleFormat(value),
        },
        axisLine: {
          show: true,
          lineStyle: { color: 'rgba(38, 40, 50, 0.2)', type: 'solid' },
        },
      },
      yAxis: {
        type: 'category', 
        data: yAxis,
        axisLabel: {
          fontSize: 8,
          color: "#262832",
          formatter: (value: string) => {
            return translateChannel(value);
          },
        },
        splitLine: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      series: series,
    };
  }, [data, translate, title]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar', excludeOptions: ['axisPointer']  }}
      options={options}
      height={height}
    ></EChartCommon>
  );
}
